import React from 'react'
import { useAppContext } from '../../providers/context'
import { SmartLinkOptional } from './link'

interface LogoProps {
  showName?: boolean
  link?: string
  size?: 'sm' | 'lg' | 'xl'
  className?: string
  showTM?: boolean
}

const logoSizes = {
  sm: 'h-[35px]',
  lg: 'h-[48px]',
  xl: 'h-[70px]',
}

/**
 * Logo component that displays the logo image and optionally the app name.
 * @param showName - Whether to show the app name (usually ReachMyTeach).
 * @param link - Optional link to navigate to when the logo is clicked.
 * @param size - Size of the logo ('sm', 'lg', 'xl').
 * @param className - Additional classes for styling.
 * @param showTM - Whether to show the trademark symbol.
 */
export const Logo: React.FC<LogoProps> = ({ showName, link = undefined, size = 'sm', className, showTM }) => {
  const context = useAppContext()
  const textClass = size === 'sm' ? 'text-xl' : size === 'lg' ? 'text-3xl text-zinc-800' : 'text-4xl text-zinc-800 ml-4'

  return (
    <SmartLinkOptional to={link} className={`flex flex-shrink-0 items-center no-underline ${className}`}>
      <LogoImage size={logoSizes[size]} />
      {showName && <LogoName textClass={textClass}>{context.state.appName}</LogoName>}
      {showTM && <span className="mt-2 self-start text-[5px] font-semibold">TM</span>}
    </SmartLinkOptional>
  )
}

const LogoImage: React.FC<{ size: string }> = ({ size }) => {
  return <img src="/logo" alt="Logo" className={`${size}`} />
}

const LogoName: React.FC<{ children: React.ReactNode; textClass: string }> = ({ children, textClass }) => {
  return <span className={`ml-2 font-bold ${textClass}`}>{children}</span>
}
